/**
 * ResultChart — renders an agent-emitted chart spec (roadmap #5) from the REAL
 * query rows the Ask/assistant already returned. The agent only chooses the SHAPE
 * (`type` + `x_field` + `series`); the data is the masked rows, never retyped by the
 * model — anti-fabrication by construction, and SQL-free.
 *
 * Standalone on purpose: the page-builder's `chart` node (`PageRenderer`) is coupled
 * to the page AST + runtime, so this is a small dedicated component over the SAME
 * `react-chartjs-2` primitives — not an extraction of that one.
 */
import * as React from "react";
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from "chart.js";
import type { ChartData, ChartOptions } from "chart.js";
import { Bar, Doughnut, Line, Pie } from "react-chartjs-2";
import { useTranslation } from "react-i18next";

import { formatPeriodLabels } from "@/lib/periodLabels";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Tooltip,
  Legend,
);

export interface ChartSpec {
  type: "bar" | "line" | "pie" | "doughnut";
  x_field: string;
  series: string[];
  title?: string;
}

/** Resolve a theme token to a concrete colour (Chart.js needs a string, not a CSS
 * var). Falls back to a sensible default off-DOM (tests / pre-mount). */
function tone(el: HTMLElement | null, varName: string, fallback: string, alpha = 1): string {
  const raw = el ? getComputedStyle(el).getPropertyValue(varName).trim() : "";
  const hsl = raw || fallback;
  return alpha >= 1 ? `hsl(${hsl})` : `hsl(${hsl} / ${alpha})`;
}

// Distinct theme hues for multi-series / pie slices, then lighter repeats.
const TONE_VARS: [string, string][] = [
  ["--accent", "217 91% 60%"],
  ["--success", "142 71% 45%"],
  ["--warning", "38 92% 50%"],
  ["--danger", "0 72% 51%"],
];

export function ResultChart({ spec, rows }: { spec: ChartSpec; rows: Record<string, unknown>[] }) {
  // Read the locale via the hook (NOT a `@/i18n` import — that module's init()
  // side-effect would boot the i18next singleton inside vitest and break every
  // key-asserting test). `i18n.language` is undefined pre-init → "en" fallback.
  const { i18n } = useTranslation();
  const boxRef = React.useRef<HTMLDivElement>(null);
  const [el, setEl] = React.useState<HTMLElement | null>(null);
  React.useLayoutEffect(() => setEl(boxRef.current), []);

  // A bucketed period column arrives as ISO boundaries (`2022-12-01T00:00:00`);
  // formatPeriodLabels turns those into friendly localized ticks ("Dic 2022")
  // and leaves any other (categorical) x-field untouched.
  const labels = formatPeriodLabels(
    rows.map((r) => String(r[spec.x_field] ?? "")),
    i18n.language || "en",
  );
  const palette = TONE_VARS.map(([v, fb]) => tone(el, v, fb));
  const fills = TONE_VARS.map(([v, fb]) => tone(el, v, fb, 0.18));
  const grid = tone(el, "--border", "0 0% 80%");
  const muted = tone(el, "--muted-foreground", "0 0% 45%");

  const isPie = spec.type === "pie" || spec.type === "doughnut";
  const num = (v: unknown) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  // Pie/doughnut: one series, one slice per row. Bar/line: one dataset per series.
  const data = isPie
    ? {
        labels,
        datasets: [
          {
            data: rows.map((r) => num(r[spec.series[0]])),
            backgroundColor: rows.map((_, i) => palette[i % palette.length]),
            borderWidth: 0,
          },
        ],
      }
    : {
        labels,
        datasets: spec.series.map((s, i) => ({
          label: s,
          data: rows.map((r) => num(r[s])),
          backgroundColor: spec.type === "line" ? fills[i % fills.length] : palette[i % palette.length],
          borderColor: palette[i % palette.length],
          borderWidth: spec.type === "line" ? 2 : 0,
          fill: spec.type === "line",
          tension: 0.3,
        })),
      };

  const legend = isPie || spec.series.length > 1;
  const common = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: legend, labels: { color: muted, boxWidth: 12 } },
    },
  };
  const axisOptions = {
    ...common,
    scales: {
      x: { ticks: { color: muted }, grid: { color: grid } },
      y: { ticks: { color: muted }, grid: { color: grid } },
    },
  };

  return (
    <div ref={boxRef} className="mt-2">
      {spec.title && (
        <div className="mb-1.5 text-sm font-medium text-foreground">{spec.title}</div>
      )}
      <div className="h-80 w-full">
        {spec.type === "pie" ? (
          <Pie data={data as ChartData<"pie", number[], string>} options={common as ChartOptions<"pie">} />
        ) : spec.type === "doughnut" ? (
          <Doughnut data={data as ChartData<"doughnut", number[], string>} options={common as ChartOptions<"doughnut">} />
        ) : spec.type === "line" ? (
          <Line data={data as ChartData<"line", number[], string>} options={axisOptions as ChartOptions<"line">} />
        ) : (
          <Bar data={data as ChartData<"bar", number[], string>} options={axisOptions as ChartOptions<"bar">} />
        )}
      </div>
    </div>
  );
}
